import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { calcApplicableBonus } from '@/hooks/useWallet';
import { getOrderPauseStatus, PAUSE_REASONS } from '@/lib/orderPause';
import { MetaEvents } from '@/lib/metaPixel';
import { trackActivity } from '@/hooks/useVisitorTracking';
import { useFavourites } from '@/hooks/useFavourites';
import { useCustomerAuth, normalizePhone } from '@/hooks/useCustomerAuth';
import ShareSheet from '@/components/feature/ShareSheet';
import CustomerLoginModal from '@/components/feature/CustomerLoginModal';
import ImageGalleryModal from './components/ImageGalleryModal';
import WalletBonusPopup from './components/WalletBonusPopup';
import ScratchCardPopup from './components/ScratchCardPopup';
import RemindMeModal from './components/RemindMeModal';
import DeliveryBusyWarning from './components/DeliveryBusyWarning';
import SearchBar from './components/SearchBar';
import SuggestedProducts from './components/SuggestedProducts';
import AddressModal from './components/AddressModal';
import CartSidebar from './components/CartSidebar';
import InstantOrderModal from './components/InstantOrderModal';

import BottomNav from '@/components/feature/BottomNav';
import { CATEGORY_DESCRIPTIONS, type CatProduct } from '@/mocks/categoryProducts';
import {
  getProductsForCategory,
  getProductCountForCategory,
  setCategoryProductsCache,
  getProductByIdFromCache,
  getVariantStock,
  getTotalStock,
  fetchProductVideo,
  fetchProductsWithVideos,
  CATEGORY_ALIAS_MAP,
} from './utils/productUtils';
import { useCartState } from './hooks/useCartState';
import { useOrderFlow } from './hooks/useOrderFlow';
import type { Address, ColorVariant, DisplayProduct } from './types';

// Scroll to top on page load
const useScrollToTop = () => {
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, []);
};

// Stock deduction kept for backward compatibility
export function deductUserProductStock(_productId: number, _quantity = 1) {}

export { getVariantStock, getTotalStock };

export default function Category() {
  useScrollToTop();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeCategory = searchParams.get('cat') ?? 'Track Pants';
  const productParam = searchParams.get('product');
  const searchParam = searchParams.get('search');

  const { user: customerUser, isLoggedIn } = useCustomerAuth();

  // Products with infinite scroll
  const [categoryProducts, setCategoryProducts] = useState<CatProduct[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProductCount, setTotalProductCount] = useState(0);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const PRODUCTS_PER_PAGE = 20; // 20 at a time — fast initial load
  const loadMoreRef = useRef<HTMLDivElement>(null); // sentinel div at bottom
  const isLoadingMoreRef = useRef(false); // prevent double-trigger
  // Track which product names have videos
  const [productsWithVideo, setProductsWithVideo] = useState<Set<string>>(new Set());

  // Initial load — first page only
  useEffect(() => {
    setProductsLoading(true);
    setCategoryProducts([]);
    setSearchResults(null);
    setCurrentPage(1);
    setHasMoreProducts(true);
    isLoadingMoreRef.current = false;
    setProductsWithVideo(new Set());

    Promise.all([
      getProductCountForCategory(activeCategory),
      getProductsForCategory(activeCategory, 1, PRODUCTS_PER_PAGE)
    ]).then(async ([count, products]) => {
      setTotalProductCount(count);
      setCategoryProducts(products);
      setCategoryProductsCache(products);
      setHasMoreProducts(products.length < count);
      setProductsLoading(false);
      // Batch fetch video info
      const uniqueNames = [...new Set(products.map(p => p.name))];
      const videoSet = await fetchProductsWithVideos(uniqueNames);
      setProductsWithVideo(videoSet);
    });
  }, [activeCategory]);

  // Load more products (called by IntersectionObserver)
  const loadMoreProducts = useCallback(async () => {
    if (!hasMoreProducts || isLoadingMoreRef.current) return;
    isLoadingMoreRef.current = true;
    setLoadingMore(true);

    const nextPage = currentPage + 1;
    const newProducts = await getProductsForCategory(activeCategory, nextPage, PRODUCTS_PER_PAGE);

    if (newProducts.length > 0) {
      setCategoryProducts(prev => {
        const updated = [...prev, ...newProducts];
        setCategoryProductsCache(updated);
        setHasMoreProducts(updated.length < totalProductCount);
        return updated;
      });
      setCurrentPage(nextPage);
      // Fetch videos for new batch
      const newUniqueNames = [...new Set(newProducts.map(p => p.name))];
      const newVideoSet = await fetchProductsWithVideos(newUniqueNames);
      setProductsWithVideo(prev => {
        const combined = new Set(prev);
        newVideoSet.forEach(name => combined.add(name));
        return combined;
      });
    } else {
      setHasMoreProducts(false);
    }

    setLoadingMore(false);
    isLoadingMoreRef.current = false;
  }, [activeCategory, currentPage, hasMoreProducts, totalProductCount]);

  // IntersectionObserver — auto trigger when sentinel div is visible
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreProducts && !isLoadingMoreRef.current) {
          loadMoreProducts();
        }
      },
      { rootMargin: '300px' } // trigger 300px before reaching bottom
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreProducts, loadMoreProducts]);

  // Cart state
  const {
    cart, isCartOpen, setIsCartOpen, showToast, toastMessage,
    cartTotal, cartCount, addItemToCart, removeFromCart, updateQuantity, clearCart, showToastMsg,
  } = useCartState();

  // Order flow
  const orderFlow = useOrderFlow({
    cart, cartTotal,
    savedAddress: null as Address | null, // will be overridden below
    clearCart,
    setIsCartOpen,
    customerUserPhone: customerUser?.phone,
  });

  // Address state
  const [savedAddress, setSavedAddress] = useState<Address | null>(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressOpenedFromCart, setAddressOpenedFromCart] = useState(false);

  // Login modal
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const requireLogin = useCallback((action: () => void) => {
    if (isLoggedIn) { action(); }
    else { setPendingAction(() => action); setShowLoginModal(true); }
  }, [isLoggedIn]);

  // Search / sort / filter
  const [searchResults, setSearchResults] = useState<CatProduct[] | null>(null);
  const [sortBy, setSortBy] = useState('popularity');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 10000]);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [inStockOnly, setInStockOnly] = useState(false);

  // Gallery
  const [galleryProduct, setGalleryProduct] = useState<CatProduct | null>(null);
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [galleryVideo, setGalleryVideo] = useState<string | undefined>(undefined);
  const [galleryVideoThumbnail, setGalleryVideoThumbnail] = useState<string | undefined>(undefined);
  const [highlightedProductId, setHighlightedProductId] = useState<number | string | null>(null);
  const productCardRefs = useRef<Map<number | string, HTMLDivElement>>(new Map());

  // Share
  const [shareProduct, setShareProduct] = useState<CatProduct | null>(null);

  // Remind Me
  const [remindProduct, setRemindProduct] = useState<CatProduct | null>(null);
  const [remindedIds, setRemindedIds] = useState<Set<number>>(() => {
    const reminders = JSON.parse(localStorage.getItem('clothybazar_reminders') || '[]');
    return new Set(reminders.map((r: { productId: number }) => r.productId));
  });

  // Pause popup
  const [showPausePopup, setShowPausePopup] = useState(false);
  const [pauseReason, setPauseReason] = useState('');

  // Delivery busy warning
  const [showDeliveryBusyWarning, setShowDeliveryBusyWarning] = useState(false);
  const [pendingOrderCallback, setPendingOrderCallback] = useState<(() => void) | null>(null);

  // Delivery unavailable
  const [showDeliveryUnavailable, setShowDeliveryUnavailable] = useState(false);
  const [deliveryUnavailableCity, setDeliveryUnavailableCity] = useState('');
  const [deliveryUnavailableDistance, setDeliveryUnavailableDistance] = useState(0);

  // Load saved address
  useEffect(() => {
    let saved: string | null = null;
    if (customerUser?.phone) {
      const normalizedPhone = normalizePhone(customerUser.phone);
      saved = localStorage.getItem(`clothybazar_address_${normalizedPhone}`);
    }
    if (!saved) saved = localStorage.getItem('userAddress');
    if (saved) {
      try { setSavedAddress(JSON.parse(saved)); } catch { /* ignore */ }
    }
  }, [customerUser?.phone]);

  // Sync wallet when modals open
  useEffect(() => {
    if (isCartOpen) { orderFlow.setApplyBonusCart(false); }
  }, [isCartOpen]);
  useEffect(() => {
    if (orderFlow.showInstantOrderModal) { orderFlow.setApplyBonusInstant(false); }
  }, [orderFlow.showInstantOrderModal]);

  const checkOrdersPaused = useCallback((): boolean => {
    const status = getOrderPauseStatus();
    if (status.paused) {
      const reasonObj = PAUSE_REASONS.find(r => r.id === status.reason);
      const displayReason = (status.reason === 'custom' && status.customReason)
        ? status.customReason : (reasonObj?.label ?? status.reason);
      setPauseReason(displayReason);
      setShowPausePopup(true);
      return true;
    }
    return false;
  }, []);

  const checkDeliveryBoysBusy = useCallback(async (onProceed: () => void) => {
    try {
      const { data: activeBoys } = await supabase.from('delivery_boys').select('name, phone').eq('status', 'active');
      if (!activeBoys || activeBoys.length === 0) { onProceed(); return; }
      const { data: activeOrders } = await supabase.from('orders').select('delivery_boy_phone')
        .in('status', ['pending', 'confirmed', 'assigned', 'out_for_delivery', 'picked_up', 'shipped'])
        .not('delivery_boy_phone', 'is', null);
      if (!activeOrders || activeOrders.length === 0) { onProceed(); return; }
      const busyPhones = new Set(activeOrders.map((o: { delivery_boy_phone: string }) => o.delivery_boy_phone).filter(Boolean));
      const freeBoys = activeBoys.filter((boy: { phone: string }) => !busyPhones.has(boy.phone));
      if (freeBoys.length === 0) { setPendingOrderCallback(() => onProceed); setShowDeliveryBusyWarning(true); }
      else { onProceed(); }
    } catch { onProceed(); }
  }, []);

  const checkDeliveryArea = useCallback((onPass: () => void) => {
    const saved = localStorage.getItem('clothybazar_location_status');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.checked && parsed.inArea === false) {
          setDeliveryUnavailableCity(parsed.city ?? 'your area');
          setDeliveryUnavailableDistance(parsed.distanceKm ?? 0);
          setShowDeliveryUnavailable(true);
          return;
        }
        if (parsed.checked && parsed.inArea === true) { onPass(); return; }
      } catch { /* ignore */ }
    }
    if (!navigator.geolocation) { onPass(); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        const adminLoc = localStorage.getItem('admin_service_location');
        let center = { lat: 30.3165, lng: 78.0322 };
        try { if (adminLoc) { const a = JSON.parse(adminLoc); center = { lat: a.lat, lng: a.lng }; } } catch { /* ignore */ }
        const R = 6371;
        const dLat = ((lat - center.lat) * Math.PI) / 180;
        const dLng = ((lng - center.lng) * Math.PI) / 180;
        const a = Math.sin(dLat / 2) ** 2 + Math.cos((center.lat * Math.PI) / 180) * Math.cos((lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
        const dist = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        if (dist > 10) { setDeliveryUnavailableCity('your area'); setDeliveryUnavailableDistance(Math.round(dist * 10) / 10); setShowDeliveryUnavailable(true); }
        else { onPass(); }
      },
      () => { onPass(); },
      { enableHighAccuracy: false, timeout: 5000 }
    );
  }, []);

  const openImageGallery = useCallback((product: CatProduct) => {
    MetaEvents.viewContent(product.name, 'product', [String(product.id)], product.price, 'INR');
    // Track product view with customer info
    const authUser = JSON.parse(localStorage.getItem('clothybazar_user') || 'null');
    trackActivity({
      page: 'category',
      action: 'product_click',
      productName: product.name,
      customerPhone: authUser?.phone ?? undefined,
      customerName: authUser?.name ?? undefined,
    });
    let imgs: string[] = [];
    if (product.selectedColor && product.colors) {
      const matchedColour = product.colors.find(c => c.name === product.selectedColor!.name);
      if (matchedColour?.images && matchedColour.images.length > 0) imgs = [...matchedColour.images];
      else if (matchedColour?.image) imgs = [matchedColour.image];
    }
    if (imgs.length === 0) {
      if (product.images && product.images.length > 0) imgs = [...product.images];
      else if (product.image) imgs.push(product.image);
    }
    imgs = [...new Set(imgs)];
    setGalleryImages(imgs);
    setGalleryProduct(product);
    fetchProductVideo(product.id, product.name).then(({ video, videoThumbnail }) => {
      setGalleryVideo(video);
      setGalleryVideoThumbnail(videoThumbnail);
    });
  }, []);

  const closeImageGallery = useCallback(() => {
    setGalleryProduct(null);
    setGalleryImages([]);
    setGalleryVideo(undefined);
    setGalleryVideoThumbnail(undefined);
    // Remove product param from URL so back button works correctly
    if (searchParams.has('product')) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('product');
      navigate(`/category?${newParams.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);

  const handleGalleryOrder = useCallback((selectedColor: ColorVariant | null, selectedSize: string) => {
    if (!galleryProduct) return;
    if (checkOrdersPaused()) return;
    const productWithColor: CatProduct = {
      ...galleryProduct,
      price: selectedColor?.price ?? galleryProduct.price,
      image: selectedColor?.image ?? galleryProduct.image,
      selectedColor: selectedColor ? { name: selectedColor.name, hex: selectedColor.hex, price: selectedColor.price } : undefined,
    };
    orderFlow.setSelectedProduct(productWithColor);
    orderFlow.setSelectedSize(selectedSize);
    if (!savedAddress) { setShowAddressModal(true); }
    else { checkDeliveryBoysBusy(() => { orderFlow.setShowInstantOrderModal(true); }); }
  }, [galleryProduct, savedAddress, checkOrdersPaused, checkDeliveryBoysBusy, orderFlow]);

  const handleGalleryAddToCart = useCallback((selectedColor: ColorVariant | null, selectedSize: string) => {
    if (!galleryProduct) return;
    let maxStock = Infinity;
    if (galleryProduct.stockBreakdown) {
      maxStock = getVariantStock(galleryProduct.stockBreakdown, galleryProduct.colors ?? [], galleryProduct.sizes, selectedColor?.name, selectedSize);
    } else if (galleryProduct.stock !== undefined && galleryProduct.stock !== null) {
      maxStock = galleryProduct.stock;
    }
    addItemToCart({
      id: galleryProduct.id, name: galleryProduct.name,
      price: selectedColor?.price ?? galleryProduct.price,
      originalPrice: galleryProduct.originalPrice, image: '',
      category: galleryProduct.category, quantity: 1, size: selectedSize,
      selectedColor: selectedColor ? { name: selectedColor.name, hex: selectedColor.hex } : undefined,
    }, maxStock);
  }, [galleryProduct, addItemToCart]);

  const handleInstantOrder = useCallback((product: CatProduct) => {
    requireLogin(() => {
      if (checkOrdersPaused()) return;
      checkDeliveryArea(() => {
        orderFlow.setSelectedProduct(product);
        if (!savedAddress || !savedAddress.lat || !savedAddress.lng) { setShowAddressModal(true); return; }
        checkDeliveryBoysBusy(() => { orderFlow.setShowInstantOrderModal(true); });
      });
    });
  }, [requireLogin, checkOrdersPaused, checkDeliveryArea, savedAddress, checkDeliveryBoysBusy, orderFlow]);

  const handleCartOrder = useCallback(() => {
    requireLogin(() => {
      if (checkOrdersPaused()) return;
      checkDeliveryArea(() => {
        if (!savedAddress || !savedAddress.lat || !savedAddress.lng) {
          setIsCartOpen(false); setAddressOpenedFromCart(true); setShowAddressModal(true); return;
        }
        checkDeliveryBoysBusy(() => { setIsCartOpen(false); orderFlow.startConfirmation('cart'); });
      });
    });
  }, [requireLogin, checkOrdersPaused, checkDeliveryArea, savedAddress, checkDeliveryBoysBusy, setIsCartOpen, orderFlow]);

  const handleAddressSave = useCallback((address: Address) => {
    setSavedAddress(address);
    setShowAddressModal(false);
    if (addressOpenedFromCart) { setAddressOpenedFromCart(false); setIsCartOpen(true); }
    else if (orderFlow.selectedProduct) { orderFlow.setShowInstantOrderModal(true); }
  }, [addressOpenedFromCart, setIsCartOpen, orderFlow]);

  // Price bounds
  const priceBounds = useMemo(() => {
    if (categoryProducts.length === 0) return { min: 0, max: 10000 };
    const prices = categoryProducts.map(p => p.price);
    return { min: Math.min(...prices), max: Math.max(...prices) };
  }, [categoryProducts]);

  useEffect(() => {
    if (!productsLoading && categoryProducts.length > 0) {
      setPriceRange([priceBounds.min, priceBounds.max]);
    }
  }, [priceBounds.min, priceBounds.max, productsLoading]);

  const sortedProducts = useMemo(() => {
    // Don't block rendering while loading more — show what we have
    const base = searchResults !== null ? searchResults : categoryProducts;
    return [...base]
      .filter(p => p.price >= priceRange[0] && p.price <= priceRange[1])
      .filter(p => inStockOnly ? !p.outOfStock : true)
      .sort((a, b) => {
        if (sortBy === 'price-low') return a.price - b.price;
        if (sortBy === 'price-high') return b.price - a.price;
        if (sortBy === 'newest') return b.id - a.id;
        return a.id - b.id;
      });
  }, [sortBy, categoryProducts, priceRange, searchResults, inStockOnly]);

  const displayedProducts: DisplayProduct[] = useMemo(() =>
    sortedProducts.map((p, i) => ({ ...p, displayId: `${p.id}-${i}` })),
    [sortedProducts]
  );
  
  // Reset pagination when filters change
  useEffect(() => {
    if (searchResults !== null) {
      // Search results show all at once (usually small set)
      setHasMoreProducts(false);
    }
  }, [searchResults]);

  // Auto-open product from URL with highlight effect
  useEffect(() => {
    if (productParam && !productsLoading && categoryProducts.length > 0) {
      const foundProduct = categoryProducts.find(p => String(p.id) === productParam || p.id === Number(productParam));
      if (foundProduct) {
        // Highlight the product card first, then open gallery
        setHighlightedProductId(foundProduct.id);
        // Scroll to the product card
        setTimeout(() => {
          const cardEl = productCardRefs.current.get(foundProduct.id);
          if (cardEl) {
            cardEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 150);
        // Open gallery after a short highlight delay
        setTimeout(() => openImageGallery(foundProduct), 700);
        // Remove highlight after animation
        setTimeout(() => setHighlightedProductId(null), 2000);
      } else {
        // Product not found, clean up URL
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('product');
        navigate(`/category?${newParams.toString()}`, { replace: true });
      }
    }
  }, [productParam, productsLoading, categoryProducts, openImageGallery, searchParams, navigate]);

  const { toggleFavourite, isFavourite } = useFavourites();

  // Refs for order execution
  const savedAddressRef = useRef(savedAddress);
  useEffect(() => { savedAddressRef.current = savedAddress; }, [savedAddress]);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 pb-20">
      {/* Customer Login Modal */}
      <CustomerLoginModal
        isOpen={showLoginModal}
        onClose={() => { setShowLoginModal(false); setPendingAction(null); }}
        onSuccess={() => {
          setShowLoginModal(false);
          if (pendingAction) { setTimeout(() => { pendingAction(); setPendingAction(null); }, 200); }
        }}
        title="Login to Order"
        subtitle="Verify OTP via phone or email to place your order!"
      />

      {/* Delivery Not Available Popup */}
      {showDeliveryUnavailable && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden">
            <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 px-6 pt-8 pb-6 text-center">
              <div className="w-20 h-20 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center mx-auto mb-4">
                <i className="ri-truck-line text-white text-4xl"></i>
              </div>
              <h2 className="text-white font-bold text-xl mb-1">Delivery Not Available</h2>
              <p className="text-white/60 text-sm">at your area right now</p>
            </div>
            <div className="px-6 py-6">
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="bg-red-50 rounded-2xl p-3 text-center border border-red-100">
                  <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1"><i className="ri-map-pin-2-fill text-red-500 text-xl"></i></div>
                  <p className="text-gray-800 font-bold text-xs">Your Area</p>
                  <p className="text-red-600 text-xs font-semibold mt-0.5 capitalize">{deliveryUnavailableCity}</p>
                  {deliveryUnavailableDistance > 0 && <p className="text-red-400 text-[10px] mt-0.5">{deliveryUnavailableDistance} km door</p>}
                </div>
                <div className="bg-green-50 rounded-2xl p-3 text-center border border-green-100">
                  <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1"><i className="ri-store-2-fill text-green-500 text-xl"></i></div>
                  <p className="text-gray-800 font-bold text-xs">Service Zone</p>
                  <p className="text-green-600 text-xs font-semibold mt-0.5">Dehradun</p>
                  <p className="text-green-400 text-[10px] mt-0.5">10km radius</p>
                </div>
              </div>
              <button onClick={() => setShowDeliveryUnavailable(false)}
                className="w-full py-3.5 rounded-2xl bg-gray-900 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-arrow-left-line"></i>Got It, Go Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Paused Popup */}
      {showPausePopup && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-sm overflow-hidden">
            <div className="relative bg-gradient-to-br from-red-600 via-red-500 to-orange-500 px-6 pt-7 pb-6 text-center overflow-hidden">
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10"></div>
              <div className="relative w-20 h-20 mx-auto mb-4">
                <div className="absolute inset-0 rounded-full bg-white/20 animate-ping"></div>
                <div className="relative w-20 h-20 rounded-full bg-white/20 border-2 border-white/30 flex items-center justify-center">
                  <i className="ri-forbid-2-fill text-white text-4xl"></i>
                </div>
              </div>
              <h2 className="text-white font-black text-xl tracking-tight">Orders Paused!</h2>
              <p className="text-red-100 text-sm mt-1">We&apos;re not accepting new orders right now</p>
              <div className="absolute bottom-0 left-0 right-0 h-3 bg-white" style={{ clipPath: 'ellipse(55% 100% at 50% 100%)' }}></div>
            </div>
            <div className="px-5 pt-4 pb-5">
              <div className="relative mb-5">
                <div className="absolute -top-3 left-4">
                  <span className="bg-red-500 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full">Reason</span>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-300 rounded-2xl px-5 pt-6 pb-4 text-center">
                  <i className="ri-double-quotes-l text-red-300 text-3xl mb-1 block"></i>
                  <p className="text-red-700 font-extrabold text-base leading-snug">{pauseReason}</p>
                </div>
              </div>
              <button onClick={() => setShowPausePopup(false)}
                className="w-full py-4 rounded-2xl bg-gray-900 text-white font-black text-sm flex items-center justify-center gap-2 hover:bg-gray-800 cursor-pointer whitespace-nowrap">
                <i className="ri-arrow-left-line text-base"></i>Got it, I&apos;ll Come Back Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Busy Warning */}
      <DeliveryBusyWarning
        isOpen={showDeliveryBusyWarning}
        onClose={() => { setShowDeliveryBusyWarning(false); setPendingOrderCallback(null); }}
        onPlaceOrder={() => {
          setShowDeliveryBusyWarning(false);
          if (pendingOrderCallback) { pendingOrderCallback(); setPendingOrderCallback(null); }
        }}
      />

      {/* Toast */}
      {showToast && (
        <div className="fixed top-24 right-4 z-50 animate-slide-in">
          <div className="gradient-primary text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2">
            <i className="ri-check-line text-lg"></i>
            <span className="text-sm font-medium">{toastMessage}</span>
          </div>
        </div>
      )}

      {/* 5-Second Order Confirmation Modal */}
      {orderFlow.showConfirmModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-sm p-7 shadow-2xl text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-emerald-100 origin-bottom"
              style={{ transform: `scaleY(${orderFlow.countdown / 5})`, transformOrigin: 'bottom', transition: 'transform 1s linear' }}></div>
            <div className="relative z-10">
              <div className="relative w-24 h-24 mx-auto mb-5">
                <svg className="w-24 h-24 -rotate-90" viewBox="0 0 96 96">
                  <circle cx="48" cy="48" r="42" fill="none" stroke="#e5e7eb" strokeWidth="6" />
                  <circle cx="48" cy="48" r="42" fill="none" stroke="#10b981" strokeWidth="6" strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 42}`}
                    strokeDashoffset={`${2 * Math.PI * 42 * (1 - orderFlow.countdown / 5)}`}
                    style={{ transition: 'stroke-dashoffset 1s linear' }} />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold text-gray-900 leading-none">{orderFlow.countdown}</span>
                  <span className="text-xs text-gray-500 mt-0.5">sec</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">Placing Your Order...</h3>
              <p className="text-gray-500 text-sm mb-6">
                {orderFlow.confirmType === 'instant' && orderFlow.selectedProduct
                  ? `${orderFlow.selectedProduct.name} · Size ${orderFlow.pendingSize} · ₹${orderFlow.selectedProduct.price - (orderFlow.applyBonusInstant ? calcApplicableBonus(orderFlow.walletBalance, orderFlow.selectedProduct.price) : 0)}`
                  : `${cart.length} item(s) · ₹${cartTotal - (orderFlow.applyBonusCart ? calcApplicableBonus(orderFlow.walletBalance, cartTotal) : 0)} (COD)`
                }
              </p>
              {savedAddress && (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2 text-left mb-4">
                  <i className="ri-map-pin-fill text-green-600 text-[11px] shrink-0"></i>
                  <p className="text-green-700 text-xs leading-tight truncate">{savedAddress.address}, {savedAddress.city}</p>
                </div>
              )}
              <button onClick={() => { orderFlow.clearCountdown(); orderFlow.setShowConfirmModal(false); if (orderFlow.confirmType === 'instant') { orderFlow.executeInstantOrderRef.current(orderFlow.pendingSize); } else { orderFlow.executeCartOrderRef.current(); } }}
                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold text-sm hover:shadow-lg transition-all cursor-pointer flex items-center justify-center gap-2 mb-3">
                <i className="ri-check-double-line text-lg"></i>Confirm Order Now
              </button>
              <button onClick={orderFlow.cancelOrderConfirmation}
                className="w-full py-3.5 rounded-2xl border-2 border-red-400 text-red-500 font-bold text-sm hover:bg-red-50 transition-all cursor-pointer flex items-center justify-center gap-2">
                <i className="ri-close-circle-line text-lg"></i>Cancel Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Address Modal */}
      <AddressModal
        isOpen={showAddressModal}
        onClose={() => { setShowAddressModal(false); if (addressOpenedFromCart) { setAddressOpenedFromCart(false); setIsCartOpen(true); } }}
        onSave={handleAddressSave}
        initialAddress={savedAddress}
      />

      {/* Instant Order Modal */}
      {orderFlow.showInstantOrderModal && orderFlow.selectedProduct && (
        <InstantOrderModal
          isOpen={orderFlow.showInstantOrderModal}
          onClose={() => orderFlow.setShowInstantOrderModal(false)}
          product={orderFlow.selectedProduct}
          selectedSize={orderFlow.selectedSize}
          setSelectedSize={orderFlow.setSelectedSize}
          savedAddress={savedAddress}
          walletBalance={orderFlow.walletBalance}
          applyBonusInstant={orderFlow.applyBonusInstant}
          setApplyBonusInstant={orderFlow.setApplyBonusInstant}
          giftCodeInstant={orderFlow.giftCodeInstant}
          setGiftCodeInstant={orderFlow.setGiftCodeInstant}
          giftCodeInstantData={orderFlow.giftCodeInstantData}
          setGiftCodeInstantData={orderFlow.setGiftCodeInstantData}
          giftCodeInstantDiscount={orderFlow.giftCodeInstantDiscount}
          setGiftCodeInstantDiscount={orderFlow.setGiftCodeInstantDiscount}
          giftCodeInstantMsg={orderFlow.giftCodeInstantMsg}
          setGiftCodeInstantMsg={orderFlow.setGiftCodeInstantMsg}
          giftCodeInstantLoading={orderFlow.giftCodeInstantLoading}
          onApplyGiftCode={orderFlow.handleApplyGiftCodeInstant}
          onConfirmOrder={orderFlow.confirmInstantOrder}
          onChangeAddress={() => { orderFlow.setShowInstantOrderModal(false); setShowAddressModal(true); }}
        />
      )}

      {/* Cart Sidebar */}
      <CartSidebar
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cart={cart}
        cartTotal={cartTotal}
        cartCount={cartCount}
        savedAddress={savedAddress}
        walletBalance={orderFlow.walletBalance}
        applyBonusCart={orderFlow.applyBonusCart}
        setApplyBonusCart={orderFlow.setApplyBonusCart}
        giftCodeCart={orderFlow.giftCodeCart}
        setGiftCodeCart={orderFlow.setGiftCodeCart}
        giftCodeCartData={orderFlow.giftCodeCartData}
        setGiftCodeCartData={orderFlow.setGiftCodeCartData}
        giftCodeCartDiscount={orderFlow.giftCodeCartDiscount}
        setGiftCodeCartDiscount={orderFlow.setGiftCodeCartDiscount}
        giftCodeCartMsg={orderFlow.giftCodeCartMsg}
        setGiftCodeCartMsg={orderFlow.setGiftCodeCartMsg}
        giftCodeCartLoading={orderFlow.giftCodeCartLoading}
        onApplyGiftCode={orderFlow.handleApplyGiftCodeCart}
        onRemoveFromCart={removeFromCart}
        onUpdateQuantity={updateQuantity}
        onCartOrder={handleCartOrder}
        onOpenAddressModal={() => { setIsCartOpen(false); setAddressOpenedFromCart(true); setShowAddressModal(true); }}
      />

      {/* Cart Button */}
      <button onClick={() => setIsCartOpen(true)}
        className="fixed top-24 right-4 z-40 w-14 h-14 rounded-full gradient-primary flex items-center justify-center text-white shadow-lg hover:scale-110 transition-transform cursor-pointer">
        <i className="ri-shopping-bag-line text-xl"></i>
        {cartCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-bold">{cartCount}</span>
        )}
      </button>

      {/* Header */}
      <header className="bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
                <i className="ri-t-shirt-line text-white text-xl"></i>
              </div>
              <span className="text-xl font-display font-bold text-gray-900 dark:text-white">
                Clothy<span className="text-primary-gradient">Bazar</span>
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                <i className="ri-home-line mr-1"></i>Home
              </Link>
              <Link to="/profile" className="w-10 h-10 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors">
                <i className="ri-user-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Category Header */}
      <div className="bg-gradient-to-r from-gray-950 via-gray-900 to-gray-800 pt-4 pb-0 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <Link to="/" className="text-gray-500 text-[11px] hover:text-gray-300 transition-colors whitespace-nowrap">Home</Link>
              <i className="ri-arrow-right-s-line text-gray-600 text-xs"></i>
              <span className="text-gray-300 text-[11px] font-medium truncate">{activeCategory}</span>
            </div>
            <div className="flex items-center gap-2">
              <h1 className="text-white font-bold text-lg leading-tight whitespace-nowrap">{activeCategory}</h1>
              <span className="shrink-0 px-2 py-0.5 rounded-full bg-white/10 text-gray-300 text-[10px] font-semibold border border-white/10">Collection</span>
            </div>
          </div>
          <p className="text-gray-500 text-[10px] truncate max-w-[140px] text-right leading-tight">
            {CATEGORY_DESCRIPTIONS[activeCategory] ?? 'Premium quality clothing'}
          </p>
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-3" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {[
            { key: 'T-Shirts', icon: 'ri-t-shirt-line' },
            { key: 'Jeans', icon: 'ri-layout-bottom-line' },
            { key: 'Hoodies', icon: 'ri-shirt-line' },
            { key: 'Shorts', icon: 'ri-scissors-cut-line' },
            { key: 'Track Pants', icon: 'ri-run-line' },
          ].map(cat => {
            const isActive = activeCategory === cat.key;
            return (
              <Link key={cat.key} to={`/category?cat=${encodeURIComponent(cat.key)}`}
                className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-t-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap relative ${isActive ? 'bg-white text-gray-900' : 'bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white'}`}>
                <i className={`${cat.icon} text-sm`}></i>{cat.key}
                {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t-full" style={{ backgroundColor: 'var(--theme-primary)' }}></span>}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {!productsLoading && categoryProducts.length > 0 && (
          <div className="mb-5">
            <SearchBar products={categoryProducts} onResultsChange={setSearchResults} initialQuery={searchParam ?? ''} />
          </div>
        )}

        {/* Sort + Filter Bar */}
        {categoryProducts.length > 0 && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
              {[
                { value: 'popularity', label: 'Popular', icon: 'ri-fire-line' },
                { value: 'newest', label: 'New First', icon: 'ri-time-line' },
                { value: 'price-low', label: 'Price ↑', icon: 'ri-sort-asc' },
                { value: 'price-high', label: 'Price ↓', icon: 'ri-sort-desc' },
              ].map(opt => (
                <button key={opt.value} onClick={() => setSortBy(opt.value)}
                  className={`shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold border transition-all cursor-pointer whitespace-nowrap ${sortBy === opt.value ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-gray-900 dark:border-white' : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:border-gray-400 dark:hover:border-gray-500'}`}>
                  <i className={`${opt.icon} text-[11px]`}></i>{opt.label}
                </button>
              ))}
              <div className="w-px h-5 bg-gray-200 dark:bg-slate-600 shrink-0 mx-1"></div>
              <button onClick={() => setShowFilterPanel(p => !p)}
                className={`shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold border transition-all cursor-pointer whitespace-nowrap ${showFilterPanel || priceRange[0] !== priceBounds.min || priceRange[1] !== priceBounds.max || inStockOnly ? 'gradient-primary text-white border-transparent' : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:border-[var(--theme-primary)] hover:text-[var(--theme-primary)]'}`}>
                <i className="ri-equalizer-2-line text-[11px]"></i>Filter
              </button>
              <span className="ml-auto shrink-0 text-xs text-gray-400 dark:text-gray-400 font-medium whitespace-nowrap">
                <span className="font-bold text-gray-700 dark:text-gray-200">{sortedProducts.length}</span>
                {sortedProducts.length !== categoryProducts.length && <span className="dark:text-gray-500"> / {categoryProducts.length}</span>} items
              </span>
            </div>

            {showFilterPanel && (
              <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl p-4 mb-3">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-gray-900 dark:text-white font-bold text-sm flex items-center gap-2"><i className="ri-equalizer-2-fill" style={{ color: 'var(--theme-primary)' }}></i>Filters</h4>
                  {(priceRange[0] !== priceBounds.min || priceRange[1] !== priceBounds.max || inStockOnly) && (
                    <button onClick={() => { setPriceRange([priceBounds.min, priceBounds.max]); setInStockOnly(false); }} className="text-xs text-red-400 hover:text-red-600 font-semibold cursor-pointer flex items-center gap-1"><i className="ri-refresh-line"></i>Reset All</button>
                  )}
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-slate-700 border border-gray-100 dark:border-slate-600 mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center"><i className="ri-checkbox-circle-fill text-emerald-600 dark:text-emerald-400 text-base"></i></div>
                    <div>
                      <p className="text-gray-900 dark:text-white text-sm font-bold">In Stock Only</p>
                      <p className="text-gray-400 dark:text-gray-500 text-[10px]">Hide out of stock products</p>
                    </div>
                  </div>
                  <button onClick={() => setInStockOnly(p => !p)} className={`relative w-11 h-6 rounded-full transition-all cursor-pointer ${inStockOnly ? 'bg-emerald-500' : 'bg-gray-200 dark:bg-slate-600'}`}>
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${inStockOnly ? 'left-5' : 'left-0.5'}`}></span>
                  </button>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-gray-700 dark:text-gray-300 text-sm font-bold flex items-center gap-1.5"><i className="ri-price-tag-3-line" style={{ color: 'var(--theme-primary)' }}></i>Price Range</p>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ color: 'var(--theme-primary)', backgroundColor: 'var(--theme-primary)15', border: '1px solid var(--theme-primary)30' }}>₹{priceRange[0]}</span>
                      <span className="text-gray-300 dark:text-gray-500 text-xs">—</span>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ color: 'var(--theme-primary)', backgroundColor: 'var(--theme-primary)15', border: '1px solid var(--theme-primary)30' }}>₹{priceRange[1]}</span>
                    </div>
                  </div>
                  <div className="space-y-3 mb-4">
                    <input type="range" min={priceBounds.min} max={priceBounds.max} step={50} value={priceRange[0]}
                      onChange={(e) => { const val = Number(e.target.value); if (val < priceRange[1]) setPriceRange([val, priceRange[1]]); }}
                      className="w-full h-1.5 rounded-full appearance-none cursor-pointer dark:bg-slate-600" style={{ accentColor: 'var(--theme-primary)' }} />
                    <input type="range" min={priceBounds.min} max={priceBounds.max} step={50} value={priceRange[1]}
                      onChange={(e) => { const val = Number(e.target.value); if (val > priceRange[0]) setPriceRange([priceRange[0], val]); }}
                      className="w-full h-1.5 rounded-full appearance-none cursor-pointer dark:bg-slate-600" style={{ accentColor: 'var(--theme-primary)' }} />
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-gray-100 dark:border-slate-700 flex items-center justify-between">
                  <p className="text-xs text-gray-500 dark:text-gray-400"><span className="font-bold text-gray-900 dark:text-white">{sortedProducts.length}</span> products match</p>
                  <button onClick={() => setShowFilterPanel(false)} className="px-5 py-2 rounded-xl gradient-primary text-white text-xs font-bold cursor-pointer whitespace-nowrap hover:opacity-90 transition-all">Apply Filters</button>
                </div>
              </div>
            )}
          </div>
        )}

        {productsLoading && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-12 h-12 rounded-full border-2 border-gray-200 dark:border-slate-600 animate-spin mb-4" style={{ borderTopColor: 'var(--theme-primary)' }}></div>
            <p className="text-gray-500 dark:text-gray-400 text-sm">Loading products...</p>

          </div>
        )}

        {sortedProducts.length === 0 && !productsLoading && (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center shadow-xl mb-4">
              <i className="ri-t-shirt-air-line text-white text-3xl"></i>
            </div>
            <h3 className="text-xl font-black text-gray-900 dark:text-white mb-1.5">{activeCategory} Collection</h3>
            <p className="text-gray-400 text-sm mb-6">Getting ready...</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-bold text-sm hover:bg-gray-700 transition-all">
              <i className="ri-home-4-line"></i>Back to Home
            </Link>
          </div>
        )}

        <div className={`grid grid-cols-2 gap-3 md:gap-5 ${sortedProducts.length === 0 ? 'hidden' : ''}`}>
          {displayedProducts.map((product) => {
            const discountPct = Math.round((1 - product.price / product.originalPrice) * 100);
            const totalStock = product.stockBreakdown ? getTotalStock(product.stockBreakdown) : null;
            const isLowStock = totalStock !== null && totalStock > 0 && totalStock <= 5;
            const isHighlighted = highlightedProductId === product.id;
            return (
              <div
                key={product.displayId}
                ref={el => {
                  if (el) productCardRefs.current.set(product.id, el);
                  else productCardRefs.current.delete(product.id);
                }}
                className={`group bg-white dark:bg-slate-900 rounded-2xl overflow-hidden flex flex-col relative transition-all duration-300 ${isHighlighted ? 'ring-4 ring-offset-2 scale-[1.03] z-10' : ''}`}
                style={{
                  boxShadow: isHighlighted
                    ? '0 0 0 4px rgba(249,115,22,0.5), 0 8px 32px rgba(249,115,22,0.35)'
                    : '0 1px 8px rgba(0,0,0,0.07)',
                  ringColor: isHighlighted ? '#f97316' : undefined,
                }}
              >
                <div className="relative overflow-hidden cursor-pointer flex-shrink-0" style={{ height: '58vw', maxHeight: '320px' }} onClick={() => openImageGallery(product)}>
                  {/* Highlight pulse overlay */}
                  {isHighlighted && (
                    <div className="absolute inset-0 z-20 pointer-events-none animate-pulse" style={{ background: 'linear-gradient(135deg, rgba(249,115,22,0.25), rgba(239,68,68,0.15))' }}>
                      <div className="absolute top-2 left-2 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-orange-500 text-white text-[10px] font-black shadow-lg">
                        <i className="ri-cursor-line text-[10px]"></i>
                        Yeh hai!
                      </div>
                    </div>
                  )}
                  <img src={product.image} alt={product.name}
                    className={`w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105 ${product.outOfStock ? 'opacity-50 grayscale' : ''}`}
                    loading="eager" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-black/10 pointer-events-none"></div>
                  <div className="absolute top-2.5 left-2.5">
                    {product.outOfStock ? (
                      <span className="px-2 py-0.5 rounded-md bg-black/70 backdrop-blur-sm text-white text-[10px] font-bold tracking-wide">SOLD OUT</span>
                    ) : (
                      <span className={`px-2 py-0.5 rounded-md text-white text-[10px] font-bold tracking-wide ${product.badge === 'NEW' ? 'bg-emerald-500' : product.badge === 'BESTSELLER' ? 'bg-amber-500' : product.badge === 'TRENDING' ? 'bg-rose-500' : product.badge === 'HOT' ? 'bg-[var(--theme-primary)]' : product.badge === 'POPULAR' ? 'bg-cyan-600' : 'bg-gray-800'}`}>
                        {product.badge}
                      </span>
                    )}
                  </div>
                  {!product.outOfStock && discountPct > 0 && (
                    <div className="absolute top-2.5 right-2.5">
                      <span className="px-2 py-0.5 rounded-md bg-black/70 backdrop-blur-sm text-white text-[10px] font-bold">-{discountPct}%</span>
                    </div>
                  )}
                  <div className="absolute bottom-2.5 left-2.5 right-2.5 flex items-center justify-between">
                    <button onClick={(e) => { e.stopPropagation(); toggleFavourite({ id: product.id, name: product.name, price: product.price, originalPrice: product.originalPrice, image: product.image, category: product.category, badge: product.badge, sizes: product.sizes }); }}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-all cursor-pointer backdrop-blur-sm ${isFavourite(product.id) ? 'bg-red-500 text-white' : 'bg-black/40 text-white hover:bg-black/60'}`}>
                      <i className={`text-sm ${isFavourite(product.id) ? 'ri-heart-fill' : 'ri-heart-line'}`}></i>
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setShareProduct(product); }}
                      className="w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/60 transition-all cursor-pointer">
                      <i className="ri-share-forward-line text-sm"></i>
                    </button>
                  </div>
                  {isLowStock && !product.outOfStock && (
                    <div className="absolute top-2.5 left-1/2 -translate-x-1/2">
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/90 backdrop-blur-sm text-white text-[9px] font-bold whitespace-nowrap">
                        <i className="ri-fire-fill text-[8px]"></i>{totalStock === 1 ? 'Last 1!' : `Only ${totalStock} left`}
                      </span>
                    </div>
                  )}
                  {/* Video Play Icon */}
                  {productsWithVideo.has(product.name) && (
                    <div className="absolute bottom-10 right-2.5 pointer-events-none">
                      <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm border border-white/20">
                        <div className="w-4 h-4 rounded-full bg-white/90 flex items-center justify-center shrink-0">
                          <i className="ri-play-fill text-gray-900" style={{ fontSize: '8px', paddingLeft: '1px' }}></i>
                        </div>
                        <span className="text-white text-[9px] font-bold">VIDEO</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-3 flex flex-col flex-1">
                  <h3 className="text-[13px] font-bold text-gray-900 dark:text-white mb-0.5 leading-snug line-clamp-1">{product.name}</h3>
                  {product.outOfStock ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-500 dark:text-red-400 mb-1.5"><i className="ri-close-circle-fill"></i>Out of Stock · Notify Below</span>
                  ) : (
                    <p className="text-gray-400 dark:text-gray-500 text-[11px] mb-1.5 line-clamp-1">{product.description}</p>
                  )}
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="text-gray-900 dark:text-white font-extrabold text-base leading-none">₹{product.price}</span>
                    <span className="text-gray-300 dark:text-gray-600 line-through text-[11px]">₹{product.originalPrice}</span>
                    {discountPct > 0 && <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ color: 'var(--theme-primary)', backgroundColor: 'var(--theme-primary)15' }}>{discountPct}% off</span>}
                  </div>

                  {product.colors && product.colors.length > 1 && (
                    <div className="flex items-center gap-1.5 mb-2.5">
                      {product.colors.slice(0, 6).map((color) => {
                        const isSelected = product.selectedColor?.name === color.name;
                        return (
                          <button key={color.name} title={color.name}
                            onClick={(e) => { e.stopPropagation(); const colourCard = sortedProducts.find(p => p.name === product.name && p.selectedColor?.name === color.name); openImageGallery(colourCard ?? product); }}
                            className={`w-5 h-5 rounded-full border-2 transition-all cursor-pointer hover:scale-110 ${isSelected ? 'border-gray-900 dark:border-white scale-110' : 'border-white dark:border-slate-600 hover:border-gray-400'}`}
                            style={{ backgroundColor: color.hex, boxShadow: '0 0 0 1px rgba(0,0,0,0.12)' }} />
                        );
                      })}
                      {product.colors.length > 6 && <span className="text-[10px] text-gray-400 dark:text-gray-500 font-semibold">+{product.colors.length - 6}</span>}
                      <span className="ml-auto text-[10px] text-gray-400 dark:text-gray-500 font-medium">{product.colors.length} colors</span>
                    </div>
                  )}

                  {product.sizes.length > 0 && (
                    <div className="flex gap-1 mb-3 flex-wrap">
                      {product.sizes.slice(0, 4).map(size => {
                        let sizeOOS = false;
                        if (product.stockBreakdown) {
                          const sv = getVariantStock(product.stockBreakdown, product.colors ?? [], product.sizes, undefined, size);
                          sizeOOS = sv === 0;
                        }
                        return (
                          <span key={size} className={`h-5 px-1.5 rounded text-[10px] font-semibold flex items-center justify-center ${sizeOOS ? 'bg-gray-100 dark:bg-slate-700 text-gray-300 dark:text-gray-500 line-through' : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300'}`}>{size}</span>
                        );
                      })}
                      {product.sizes.length > 4 && <span className="h-5 px-1.5 rounded bg-gray-100 dark:bg-slate-700 text-[10px] text-gray-400 dark:text-gray-500 flex items-center">+{product.sizes.length - 4}</span>}
                    </div>
                  )}

                  <div className="mt-auto space-y-2">
                    {product.outOfStock ? (
                      <button onClick={() => setRemindProduct(product)}
                        className={`w-full py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 whitespace-nowrap cursor-pointer transition-all ${remindedIds.has(product.id) ? 'bg-emerald-50 border border-emerald-400 text-emerald-600' : 'bg-gray-900 text-white hover:bg-gray-700'}`}>
                        <i className={remindedIds.has(product.id) ? 'ri-notification-badge-fill' : 'ri-notification-3-line'}></i>
                        {remindedIds.has(product.id) ? 'Reminder Set ✓' : 'Notify Me'}
                      </button>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold">
                            <i className="ri-time-line text-emerald-500"></i>30 Min Delivery
                          </span>
                          <span className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-bold">
                            <i className="ri-money-rupee-circle-line text-amber-500"></i>COD
                          </span>
                        </div>
                        <button onClick={() => openImageGallery(product)}
                          className="w-full py-2.5 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-bold text-xs flex items-center justify-center gap-1.5 hover:bg-gray-700 dark:hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap active:scale-95">
                          <i className="ri-eye-line text-sm"></i>View &amp; Order
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Infinite Scroll Sentinel — IntersectionObserver watches this */}
        {!searchResults && (
          <div ref={loadMoreRef} className="w-full flex flex-col items-center py-6">
            {loadingMore && (
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <div className="w-5 h-5 rounded-full border-2 border-gray-200 animate-spin" style={{ borderTopColor: 'var(--theme-primary)' }}></div>
                <span className="text-xs font-medium">Loading more products...</span>
              </div>
            )}
          </div>
        )}

        {/* End of list message */}
        {!searchResults && !hasMoreProducts && sortedProducts.length > 0 && (
          <div className="w-full flex flex-col items-center py-8 gap-3">
            <div className="w-12 h-1 rounded-full bg-gray-200 dark:bg-slate-600"></div>
            <p className="text-gray-400 text-xs font-medium">All done! Total {totalProductCount} products</p>
          </div>
        )}

        {/* Search results end message */}
        {searchResults && sortedProducts.length > 0 && (
          <div className="w-full flex flex-col items-center py-8 gap-3">
            <div className="w-12 h-1 rounded-full bg-gray-200 dark:bg-slate-600"></div>
            <p className="text-gray-400 text-xs font-medium">{sortedProducts.length} search results found</p>
          </div>
        )}
      </div>

      {/* Share Sheet */}
      <ShareSheet isOpen={shareProduct !== null} onClose={() => setShareProduct(null)}
        productName={shareProduct?.name ?? ''} productPrice={shareProduct?.price ?? 0}
        productCategory={shareProduct?.category ?? ''} productImage={shareProduct?.image}
        productId={shareProduct?.id} />

      {/* Image Gallery Modal */}
      <ImageGalleryModal
        isOpen={galleryProduct !== null} onClose={closeImageGallery}
        images={galleryImages} productName={galleryProduct?.name ?? ''}
        price={galleryProduct?.price ?? 0} originalPrice={galleryProduct?.originalPrice ?? 0}
        colors={galleryProduct?.colors}
        defaultSelectedColor={galleryProduct?.selectedColor ? (galleryProduct.colors?.find(c => c.name === galleryProduct.selectedColor!.name) ?? null) : null}
        sizes={galleryProduct?.sizes} description={galleryProduct?.description}
        stockBreakdown={galleryProduct?.stockBreakdown}
        video={galleryVideo} videoThumbnail={galleryVideoThumbnail}
        onOrder={handleGalleryOrder} onAddToCart={handleGalleryAddToCart}
        allProducts={sortedProducts} currentProductId={galleryProduct?.id}
        onSimilarProductClick={(product) => openImageGallery(product)}
      />

      {/* Remind Me Modal */}
      {remindProduct && (
        <RemindMeModal product={remindProduct} onClose={() => {
          setRemindedIds(new Set(JSON.parse(localStorage.getItem('clothybazar_reminders') || '[]').map((r: { productId: number }) => r.productId)));
          setRemindProduct(null);
        }} />
      )}

      {/* Scratch Card Popup */}
      <ScratchCardPopup isOpen={orderFlow.showScratchCard} paidAmount={orderFlow.scratchPaidAmount} scratchPercent={orderFlow.scratchPercent}
        onClose={(earned, newTotal) => { orderFlow.setShowScratchCard(false); orderFlow.setEarnedBonusAmount(earned); orderFlow.setNewWalletTotal(newTotal); orderFlow.setShowBonusPopup(true); }} />

      {/* Wallet Bonus Popup */}
      <WalletBonusPopup isOpen={orderFlow.showBonusPopup} earnedBonus={orderFlow.earnedBonusAmount}
        newWalletTotal={orderFlow.newWalletTotal} paidAmount={orderFlow.bonusPaidAmount}
        onClose={orderFlow.handleBonusPopupClose} />

      {/* Bottom Navigation */}
      <BottomNav cartCount={cartCount} onOpenCart={() => setIsCartOpen(true)} />
    </div>
  );
}
